import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import {
    CustomFormControlErrorStateMatcher,
    USER_INPUT_DEBOUNCE_TIME,
    getSheetsInSparsArraysFromFile,
} from '@modeso/aumico-lib-core-fe';
import { ReportType } from '@modeso/aumico-lib-core-fe/src/lib';
import {
    AccountType,
    AnnualReportStep,
    AutofillOptions,
    FinancialStatementTypes,
    IAccount,
    IAccountFilter,
    IAnnualReport,
    IBalanceSwitchAccount,
    IExtraExcelContent,
    IFinancialStatementImportDefinition,
    IFinancialStatementInterpretation,
    IFinancialStatementSource,
} from '@modeso/types__aumico-lib-annualreport-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { ActionsSubject, Store } from '@ngrx/store';
import Debug from 'debug';
import fastDeepEqual from 'fast-deep-equal';
import { cloneDeep } from 'lodash-es';
import { Observable, combineLatest, merge, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, first, map } from 'rxjs/operators';
import { ProjectsOverviewActions, ProjectsOverviewState } from '../../../../../aumico-lib-projects-fe/src/lib';
import { selectActiveProjectId } from '../../../../../aumico-lib-projects-fe/src/lib/reducers/projects-overview.reducer';
import { localizationKeys } from '../../constants/localizationKeys';
import { AnnualReportMetaInformationDTO } from '../../dtos/annualReportMetaInformation.dto';
import { PartnerExcelImportFileType } from '../../enums/partner-excel-import-file-type.enum';
import {
    DATEV_ERROR,
    DatevExcelHelper,
    SpecialFLIs,
    TaxonomyDefinitions,
    TaxonomyDefinitionsWithErrorInformation,
} from '../../helpers/datev.excel.helper';
import { AumicoSections } from '../../helpers/datev-ai.adapter';
import { FinancialStatementImportFormHelper } from '../../helpers/financial-statement-import-form.helper';
import { IFinancialStatementImportOptions } from '../../interfaces/IFinancialStatementImportOptions.interface';
import { IHighlightedRows } from '../../interfaces/IHighlightedRows.interface';
import { ISpreadSheet } from '../../interfaces/ISpreadSheet';
import { ISpreadSheetClick } from '../../interfaces/ISpreadSheetClick.interface';
import { ProjectToCopyFrom } from '../../interfaces/project-to-copy-from.interface';
import { AnnualReportNavigationProvider } from '../../provider/annual-report-navigation.provider';
import { AnnualReportProvider } from '../../provider/annualReport.provider';
import { MatDialog } from '@angular/material/dialog';
import {
    DatevTaxonomyPreviewDialogComponent,
    TaxonomyPreviewDialogData,
} from '../../components/datev-taxonomy-preview-dialog/datev-taxonomy-preview-dialog.component';
import { AiAgentJobRequest, AiAgentProvider } from '@modeso/aumico-lib-ai-agents-fe';
import { AiFixerDelegate } from '../../interfaces/ai-fixer-delegate.interface';



const debug = Debug('aumico:aumico-lib-annualreport-fe:FinancialStatementImportFormPage');

interface FormValidationState {
    formControlsComplete: boolean;
    incomeStatementCompleted: boolean;
    balanceCompleted: boolean;
    specialFlisFormCompleted: boolean;
    accountFilterFormValid: boolean;
    sameSheetSelectionComplete: boolean;
}

@UntilDestroy()
@Component({
    selector: 'aumico-annualReport-financial-statement-import-form-page',
    templateUrl: 'financial-statement-import-form.page.html',
    styleUrls: ['financial-statement-import-form.page.scss'],
})
export class FinancialStatementImportFormPage implements OnInit, OnChanges {
    PartnerExcelImportFileType = PartnerExcelImportFileType;
    importReqognizedFormat: PartnerExcelImportFileType = PartnerExcelImportFileType.UNKNOWN;
    importedTaxonomy: TaxonomyDefinitions | undefined;
    importedTaxonomyErros: DATEV_ERROR[] = [];
    extraExcelContent: IExtraExcelContent | null = null;
    unmatchableBalanceAccounts: IAccount[] = [];
    unmatchableIncomeAccounts: IAccount[] = [];
    newShareholderBalanceAccounts: IAccount[] = [];
    newShareholderIncomeAccounts: IAccount[] = [];
    activeAnnualReport: IAnnualReport | null = null;
    displayAccounts: IAccount[] = [];
    accountsWhichExistInPreviousYearMoreThanThisYearBalance: IAccount[] = [];
    accountsWhichExistInPreviousYearMoreThanThisYearIncome: IAccount[] = [];

    @Input() public set injectedProviderSetter(injectedProvider: AnnualReportProvider) {
        if (injectedProvider) {
            this.provider = injectedProvider;
        }
    }

    @Output() importFormCompleted = new EventEmitter<boolean>();
    loseFocus: {};
    @Output() fileChanged = new EventEmitter<boolean>();
    @Output() accountsToMap = new EventEmitter<IAccount[]>();
    @Output() extraExcelContentChanged = new EventEmitter<IExtraExcelContent | null>();

    @Output() selectedAnnualReportWithAutofillOptions = new EventEmitter<Record<AutofillOptions, string>>();

    /**
     * Emitted when a DATEV file is uploaded that contains structural errors.
     * The raw ISpreadSheet is emitted so the parent can build an AI job request
     * from the same data the onboarding tool's hierarchy-classifier would use.
     */
    @Output() datevSpreadSheetWithErrors = new EventEmitter<ISpreadSheet>();

    // ── AI Fixer — delegate from parent ───────────────────────────────

    /**
     * Single delegate object that bundles all AI-fixer state and callbacks.
     * The parent creates a new reference whenever AI state changes, which
     * triggers `ngOnChanges` here. Replaces the former 6 @Inputs + 2 @Outputs.
     */
    @Input() aiDelegate: AiFixerDelegate | null = null;

    /** True once the taxonomy was repaired by the AI fixer — drives the "Fixed with AI" badge. */
    isAiFixed = false;

    /**
     * AI proposal sections extracted as soon as the job completes (before accept/reject).
     * Used to drive the preview button and datev-fixer-result tree while the panel is open.
     * Cleared when the AI panel is hidden (accepted or rejected).
     */
    aiProposalSections: AumicoSections | null = null;

    spreadSheetData: ISpreadSheet = { sheets: [] };
    balanceSpreadSheetData: ISpreadSheet = { sheets: [] };
    incomeSpreadSheetData: ISpreadSheet = { sheets: [] };
    enableImportOptions = true;
    local = localizationKeys;

    balanceTitleKey = 'aumico_annual_report_import_form_balance_title';
    incomeStatementTitleKey = 'aumico_annual_report_import_form_income_title';
    balanceInstructionTextKey = 'aumico_annual_report_import_form_balance_instruction_text';
    balanceInstructionLabelKey = 'aumico_annual_report_import_form_balance_instruction_label';
    incomeInstructionTextKey = 'aumico_annual_report_import_form_income_instruction_text';
    incomeInstructionLabelKey = 'aumico_annual_report_import_form_income_instruction_label';
    importBalanceAndIncomeSeparately = false;

    balanceCompleted: boolean;
    incomeStatementCompleted: boolean;
    specialFlisFormCompleted = true;
    showInterpretation: boolean;
    showIncomeImportDefinition: boolean;
    hideDefaultSettings: boolean = false;
    priorYearUnmatchableAccountsDetectedBalance: boolean = false;
    priorYearUnmatchableAccountsDetectedBalanceFixed: boolean = false;
    priorYearUnmatchableAccountsDetectedBalanceFixedColNo: any;
    priorYearUnmatchableAccountsDetectedIncome: boolean = false;
    priorYearUnmatchableAccountsDetectedIncomeFixed: boolean = false;
    priorYearUnmatchableAccountsDetectedIncomeFixedColNo: any;
    sameSheetFlag: boolean;

    clickedCell: ISpreadSheetClick;
    definitionToCopy: IFinancialStatementImportDefinition;

    private allAccounts: IAccount[] = [];
    private filteredAccounts: IAccount[] = [];
    private balanceAccounts: IAccount[] = [];
    private incomeAccounts: IAccount[] = [];

    private isAccountFilterFormValid = true;

    balanceHighlightedRows: IHighlightedRows = {
        sheetName: '',
        rows: [],
        unmatchableAccountRows: [],
    };
    incomeHighlightedRows: IHighlightedRows = {
        sheetName: '',
        rows: [],
        unmatchableAccountRows: [],
    };

    // we set them as input incase of edit mode or autofill
    @Input() balanceFinancialStatementSource: IFinancialStatementSource = {} as IFinancialStatementSource;
    @Input() incomeFinancialStatementSource: IFinancialStatementSource = {} as IFinancialStatementSource;

    @Output() balanceFinancialStatementSourceOutput = new EventEmitter<IFinancialStatementSource>();
    @Output() incomeFinancialStatementSourceOutput = new EventEmitter<IFinancialStatementSource>();
    @Output() importedTaxonomyConstructed = new EventEmitter<TaxonomyDefinitions>();

    formGroup = new UntypedFormGroup({});

    @Input() autoFillApplied: boolean;
    @Input() noPreviousYearData: boolean;
    @Input() isPreviousYearDataTakenFromPreviousBalanceAnnualReport: boolean;
    @Input() isPreviousYearDataTakenFromPreviousIncomeAnnualReport: boolean;
    @Input() previousBalanceAccounts: IAccount[] = [];
    @Input() previousIncomeAccounts: IAccount[] = [];
    @Input() previousBalanceSwitchAccounts: IBalanceSwitchAccount[];
    @Input() autofillOptions: Record<AutofillOptions, string>;
    @Input() activeAnnualReportId: string;
    @Input() selectedAccountingBasisId: string | undefined;
    @Input() selectedCompanyLegalFormId: string | undefined;
    @Input() uploadAndMappingAutoFillAnnualReportIdExist: string;
    @Input() uploadAndMappingAutoFillAnnualReportToggleDisabled: boolean;
    @Input() reportLocale: string | undefined;
    @Input() userLanguage: string = 'de-ch';

    @Input() showShareholderInformation = false;

    // For saved/autofill data coming as input
    activeBalanceFinancialStatementImportDefinition: IFinancialStatementImportDefinition | undefined;
    activeIncomeFinancialStatementImportDefinition: IFinancialStatementImportDefinition | undefined;

    // For temporary data that the user just entered so that it would be used for autofill incase of file deletion & re-upload
    currentFilledBalanceFinancialStatementImportDefinition: IFinancialStatementImportDefinition | undefined;
    currentFilledIncomeFinancialStatementImportDefinition: IFinancialStatementImportDefinition | undefined;

    annualReportsList$: Observable<AnnualReportMetaInformationDTO[]>;
    projectsList$: Observable<ProjectToCopyFrom[]>;

    balanceRangeErrorStateMatcher = new CustomFormControlErrorStateMatcher(['invalidBalanceRange']);
    incomeRangeErrorStateMatcher = new CustomFormControlErrorStateMatcher(['invalidIncomeRange']);
    canUseUncovershorfall = false;
    canUseEquity = false;
    readonly invalidBalanceRange = 'invalidBalanceRange';
    readonly invalidIncomeRange = 'invalidIncomeRange';
    readonly activeAR$;
    public readonly activeArNavigation$: Observable<AnnualReportStep[]> | undefined;
    constructor(
        private provider: AnnualReportProvider,
        private store: Store<ProjectsOverviewState>,
        private datevExcelHelper: DatevExcelHelper,
        navigationProvider: AnnualReportNavigationProvider,
        private actionsListener$: ActionsSubject,
        private readonly dialog: MatDialog,
        private readonly aiAgentProvider: AiAgentProvider,
    ) {
        this.activeAR$ = provider.getActiveAnnualReport$();
        this.activeArNavigation$ = navigationProvider.getActiveArNavigation$();

        // Combine projects list with annual reports list
        const projectsObservable$ = combineLatest([
            this.actionsListener$.pipe(ofType(ProjectsOverviewActions.projectsListFetched)),
            this.store.select(selectActiveProjectId)
        ]).pipe(
            map(([payload, projectId]) =>
                payload.response.projectsMetaInformation
                    .filter((project) => project != null && project.companyName != null)
                    .filter((project) => project != null && project.accountingBasis != null)
                    .filter((project) => project.projectId !== projectId)
            ),
            untilDestroyed(this)
        );

        const annualReportsMetaObservable$ = this.provider.getAnnualReportsListToCopyFrom$().pipe(
            filter((annualReportsList) => annualReportsList != null), // Filter null values
            map((annualReportsList) =>
                annualReportsList
                    .filter((annualMetaInfo) => annualMetaInfo != null && annualMetaInfo.companyName != null)
                    .filter((annualMetaInfo) => annualMetaInfo != null && annualMetaInfo.legalForm != null)
                    .filter((annualMetaInfo) => annualMetaInfo != null && annualMetaInfo.accountingBasis != null)
                    .filter((annualMetaInfo) => annualMetaInfo != null && annualMetaInfo.locale != null)
                    .filter(
                        (annualMetaInfo) =>

                            annualMetaInfo.locale.toLocaleLowerCase() === this.reportLocale.toLocaleLowerCase(),
                    ),
            ),
            untilDestroyed(this)
        );

        this.store.dispatch(ProjectsOverviewActions.fetchProjectsList({ filter: { reportTypeIds: [ReportType.ANNUAL] } }));
        this.provider.dispatchAnnualReportsListToCopyFrom();

        // Combine both observables
        this.projectsList$ = combineLatest([projectsObservable$, annualReportsMetaObservable$]).pipe(
            map(([projects, annualReportsMeta]) => {
                return projects
                    .map((project): ProjectToCopyFrom | null => {
                        // Find matching annual report by annual report meta id
                        const matchingAnnualReport = annualReportsMeta.find(
                            (metaInfo) =>
                                metaInfo._id === project.annualReportMetaData
                        );

                        if (!matchingAnnualReport) {
                            return null; // Remove project from projects
                        }
                        return {
                            ...project,
                            annualReportEncryptedId: matchingAnnualReport.annualReportEncryptedId,
                            legalForm: matchingAnnualReport.legalForm,
                        };
                    })
                    .filter((project): project is ProjectToCopyFrom => project !== null);
            }),
            untilDestroyed(this)
        );
    }

    toggleSettings() {
        this.hideDefaultSettings = !this.hideDefaultSettings;
    }

    /**
     * Opens the full-screen taxonomy preview dialog showing the AI-fixed sections.
     * Only callable when aiProposalSections is present (button is disabled otherwise).
     */
    openTaxonomyPreview(): void {
        const sections = this.aiProposalSections ?? this.aiDelegate?.fixedSections;
        if (!sections) return;
        this.aiAgentProvider.activeSessionId$.pipe(first()).subscribe(sessionId => {
            this.dialog.open<DatevTaxonomyPreviewDialogComponent, TaxonomyPreviewDialogData>(
                DatevTaxonomyPreviewDialogComponent,
                {
                    data: { sections, sessionId },
                    maxWidth: '92vw',
                    width: '92vw',
                    maxHeight: '90vh',
                    panelClass: 'taxonomy-preview-dialog-panel',
                },
            );
        });
    }

    /**
     * Called when the AI job completes — extracts the proposal sections so the
     * datev-fixer-result tree and Preview button are active before accept/reject.
     */
    onAiCompleted(output: Record<string, unknown>): void {
        this.aiProposalSections = (output['sections'] as AumicoSections) ?? null;
    }

    ngOnChanges(changes: SimpleChanges): void {
        // ── AI delegate changes ───────────────────────────────────────
        if (changes['aiDelegate']) {
            const prev = changes['aiDelegate'].previousValue as AiFixerDelegate | null;
            const curr = changes['aiDelegate'].currentValue as AiFixerDelegate | null;

            // Clear proposal sections when the AI panel is hidden (accepted or rejected)
            if (prev?.showPanel && !curr?.showPanel) {
                this.aiProposalSections = null;
            }

            // Taxonomy was fixed — switch into the normal DATEV-master flow
            if (curr?.fixedTaxonomy && curr.fixedTaxonomy !== prev?.fixedTaxonomy) {
                this.importedTaxonomy = curr.fixedTaxonomy;
                this.importedTaxonomyErros = [];
                this.importReqognizedFormat = PartnerExcelImportFileType.DATEV_MASTER;
                this.isAiFixed = true;
                this.hideDefaultSettings = true;
                this.importedTaxonomyConstructed.emit(curr.fixedTaxonomy);
                this.checkFormCompletion();
            }
        }
        if (changes && changes['balanceFinancialStatementSource']) {
            this.balanceFinancialStatementSource = changes['balanceFinancialStatementSource'].currentValue
                ? changes['balanceFinancialStatementSource'].currentValue
                : ({} as IFinancialStatementSource);
            if (this.balanceFinancialStatementSource.importDefinition != null) {
                this.activeBalanceFinancialStatementImportDefinition = cloneDeep(
                    this.balanceFinancialStatementSource.importDefinition,
                );
                this.priorYearUnmatchableAccountsDetectedBalance = false;
                this.setAccounts(this.balanceFinancialStatementSource);
            }
        }
        if (changes && changes['incomeFinancialStatementSource']) {
            this.incomeFinancialStatementSource = changes['incomeFinancialStatementSource'].currentValue
                ? changes['incomeFinancialStatementSource'].currentValue
                : ({} as IFinancialStatementSource);
            if (this.incomeFinancialStatementSource.importDefinition != null) {
                this.activeIncomeFinancialStatementImportDefinition = cloneDeep(
                    this.incomeFinancialStatementSource.importDefinition,
                );
                this.priorYearUnmatchableAccountsDetectedIncome = false;
                this.setAccounts(this.incomeFinancialStatementSource);
            }
        }

    }

    ngOnInit(): void {
        this.checkStateValues();
        this.subscribeToActiveAR();
        this.subscribeToActiveArNavigation();
        this.balanceFinancialStatementSource.type = FinancialStatementTypes.BALANCE;

        this.incomeFinancialStatementSource.type = FinancialStatementTypes.INCOME;

        this.initializeFormGroup();

        this.setInitialAccounts();

        this.annualReportsList$ = this.provider.getAnnualReportsListToCopyFrom$().pipe(
            map((annualReportsList) =>
                annualReportsList
                    .filter((annualMetaInfo) => annualMetaInfo != null && annualMetaInfo.companyName != null)
                    .filter((annualMetaInfo) => annualMetaInfo != null && annualMetaInfo.legalForm != null)
                    .filter((annualMetaInfo) => annualMetaInfo != null && annualMetaInfo.accountingBasis != null)
                    .filter((annualMetaInfo) => annualMetaInfo != null && annualMetaInfo.locale != null)
                    .filter(
                        (annualMetaInfo) =>
                            annualMetaInfo.annualReportEncryptedId !== this.activeAnnualReportId &&
                            annualMetaInfo.locale.toLocaleLowerCase() === this.reportLocale.toLocaleLowerCase(),
                    ),
            ),
        );

        this.provider
            .getFinancialStatmentImportLoseFocusChange$()
            .pipe(untilDestroyed(this))
            .subscribe((loseFocus) => {
                this.loseFocus = loseFocus;
            });
    }

    private subscribeToActiveAR(): void {
        this.activeAR$.pipe(untilDestroyed(this)).subscribe((newActiveAR) => {
            this.handleNewActiveAR(newActiveAR);
        });
    }

    private subscribeToActiveArNavigation(): void {
        this.activeArNavigation$.pipe(untilDestroyed(this)).subscribe((arNavigations) => {
            this.handleArNavigations(arNavigations);
        });
    }

    private handleNewActiveAR(activeAR: IAnnualReport): void {
        this.activeAnnualReport = activeAR;
        if (activeAR && activeAR.annualReportTemplate && activeAR.annualReportTemplate.settings) {
            this.canUseUncovershorfall = activeAR.annualReportTemplate.settings.uncoveredShortfall;
        } else {
            this.canUseUncovershorfall = false;
        }
    }

    private handleArNavigations(arNavigations: AnnualReportStep[]) {
        this.canUseEquity = arNavigations.includes(AnnualReportStep.EMS);
    }

    private setInitialAccounts(): void {
        if (this.balanceFinancialStatementSource != null && this.incomeFinancialStatementSource != null) {
            this.priorYearUnmatchableAccountsDetectedIncome = false;
            this.priorYearUnmatchableAccountsDetectedBalance = false;
            this.setAccounts(this.balanceFinancialStatementSource);
            this.setAccounts(this.incomeFinancialStatementSource);
        }
    }

    initializeFormGroup(): void {
        const balanceSingleSheetAccountsRanges = this.balanceFinancialStatementSource.singleSheetAccountsRanges;
        const incomeSingleSheetAccountRanges = this.incomeFinancialStatementSource.singleSheetAccountsRanges;

        this.formGroup = new UntypedFormGroup(
            {
                balanceAccountsStartRow: new UntypedFormControl(
                    balanceSingleSheetAccountsRanges != null ? balanceSingleSheetAccountsRanges.start : null,
                    [Validators.required],
                ),

                balanceAccountsEndRow: new UntypedFormControl(
                    balanceSingleSheetAccountsRanges != null ? balanceSingleSheetAccountsRanges.end : null,
                    [Validators.required],
                ),

                incomeAccountsStartRow: new UntypedFormControl(
                    incomeSingleSheetAccountRanges != null ? incomeSingleSheetAccountRanges.start : null,
                    [Validators.required],
                ),

                incomeAccountsEndRow: new UntypedFormControl(
                    incomeSingleSheetAccountRanges != null ? incomeSingleSheetAccountRanges.end : null,
                    [Validators.required],
                ),
            },
            FinancialStatementImportFormHelper.rangeCheckValidator,
        );

        this.monitorFormGroupChanges();
    }

    onBalanceAndIncomeCombinedFileChanged(file: File): void {
        this.priorYearUnmatchableAccountsDetectedBalance = false;
        this.priorYearUnmatchableAccountsDetectedIncome = false;
        this.newShareholderBalanceAccounts = [];
        this.newShareholderIncomeAccounts = [];
        this.displayAccounts = [];
        this.importReqognizedFormat = PartnerExcelImportFileType.UNKNOWN;
        this.extraExcelContent = null;
        this.extraExcelContentChanged.emit(this.extraExcelContent);
        let parsedFileObservable: Observable<ISpreadSheet>;
        if (file != null) {
            parsedFileObservable = getSheetsInSparsArraysFromFile(file);
        } else {
            parsedFileObservable = of(null);
        }
        parsedFileObservable.pipe(first(), untilDestroyed(this)).subscribe((parsedFile) => {
            if (parsedFile == null) {
                this.enableImportOptions = true;
                this.spreadSheetData = { sheets: [] };
                this.incomeSpreadSheetData = { sheets: [] };
                this.balanceSpreadSheetData = { sheets: [] };
                this.showIncomeImportDefinition = false;
                this.showInterpretation = false;
            } else {
                if (!this.automaticallySetImportSettingsIfPossible(parsedFile)) {
                    this.enableImportOptions = false;
                    this.spreadSheetData = parsedFile;
                    this.incomeSpreadSheetData = parsedFile;
                    this.balanceSpreadSheetData = parsedFile;
                }
            }
            this.handleEmittersOnFileChange(parsedFile);
        });
    }

    automaticallySetImportSettingsIfPossible(spreadSheetData: ISpreadSheet): boolean {
        this.importReqognizedFormat = this.datevExcelHelper.isKnownFileFormat(spreadSheetData);
        const format = this.importReqognizedFormat;
        if (format > PartnerExcelImportFileType.UNKNOWN) {
            this.spreadSheetData = spreadSheetData;
            this.incomeSpreadSheetData = spreadSheetData;
            this.balanceSpreadSheetData = spreadSheetData;
            const settings = this.datevExcelHelper.getImportSettings();
            this.onBalanceImportDefinitionCompleted(settings.balanceSheet);
            this.onIncomeImportDefinitionCompleted(settings.incomeStatement);
            this.setInterpretationFormValue(settings.normalization);
            this.setAccountFilterFormValue(settings.filter);

            this.extraExcelContent = this.datevExcelHelper.extractExtraExcelContent(spreadSheetData);
            this.extraExcelContentChanged.emit(this.extraExcelContent);

            // If this is a Datev Master file, then we need to create the ITemplateEntityDTO
            // this creates the rawIncomeTemplate and rawBalanceTemplate to be used in FinancialStatementAccountMappingFormComponent
            // in this case we don't need to load any template data from the backend

            try {
                const importedTaxonomyWithErrors: TaxonomyDefinitionsWithErrorInformation =
                    this.datevExcelHelper.createTaxonomy(
                        spreadSheetData,
                        this.canUseUncovershorfall,
                        this.canUseEquity,
                    );
                if (importedTaxonomyWithErrors.errors.length == 0) {
                    this.importedTaxonomy = importedTaxonomyWithErrors.taxonomyDefinitions;
                    this.importedTaxonomyErros = importedTaxonomyWithErrors.errors;
                    this.importedTaxonomyConstructed.emit(this.importedTaxonomy);
                    this.checkFormCompletion();
                    this.hideDefaultSettings = true;
                    return true;
                } else {
                    this.importedTaxonomy = undefined;
                    this.importedTaxonomyErros = importedTaxonomyWithErrors.errors;
                    this.hideDefaultSettings = true;
                    this.datevSpreadSheetWithErrors.emit(spreadSheetData);
                    return false;
                }
            } catch (error) {
                this.importedTaxonomy = undefined;
                console.error(error);
                this.datevSpreadSheetWithErrors.emit(spreadSheetData);
                this.importedTaxonomyErros = [DATEV_ERROR.MALFORMATED_FILE];
                this.hideDefaultSettings = true;
                return false;
            }
        } else {
            this.hideDefaultSettings = false;
            return false;
        }
    }

    onIncomeSeparateFileChanged(file: File): void {
        this.priorYearUnmatchableAccountsDetectedIncome = false;
        this.newShareholderIncomeAccounts = [];
        this.importReqognizedFormat = PartnerExcelImportFileType.UNKNOWN;
        let parsedFileObservable: Observable<ISpreadSheet>;
        if (file != null) {
            parsedFileObservable = getSheetsInSparsArraysFromFile(file);
        } else {
            parsedFileObservable = of(null);
        }
        parsedFileObservable.pipe(first(), untilDestroyed(this)).subscribe((parsedFile: ISpreadSheet) => {
            if (parsedFile == null) {
                if (this.balanceSpreadSheetData.sheets.length === 0) {
                    // only show import options if no file is uploaded for both balance & income
                    this.enableImportOptions = true;
                    this.showIncomeImportDefinition = false;
                    this.showInterpretation = false;
                }
                this.incomeSpreadSheetData = { sheets: [] };
                this.incomeStatementCompleted = false;
            } else {
                this.enableImportOptions = false;
                this.incomeSpreadSheetData = parsedFile;
            }
            this.updateDisplayAccounts();
            this.handleEmittersOnFileChange(parsedFile);
        });
    }

    onBalanceSeparateFileChanged(file: File): void {
        this.priorYearUnmatchableAccountsDetectedBalance = false;
        this.newShareholderBalanceAccounts = [];
        this.importReqognizedFormat = PartnerExcelImportFileType.UNKNOWN;
        let parsedFileObservable: Observable<ISpreadSheet>;
        if (file != null) {
            parsedFileObservable = getSheetsInSparsArraysFromFile(file);
        } else {
            parsedFileObservable = of(null);
        }
        parsedFileObservable.pipe(first(), untilDestroyed(this)).subscribe((parsedFile) => {
            if (parsedFile == null) {
                if (this.incomeSpreadSheetData.sheets.length === 0) {
                    // only show import options if no file is uploaded for both balance & income
                    this.enableImportOptions = true;
                }
                this.balanceSpreadSheetData = { sheets: [] };
                this.balanceCompleted = false;
            } else {
                this.enableImportOptions = false;
                this.balanceSpreadSheetData = parsedFile;
            }
            this.updateDisplayAccounts();
            this.handleEmittersOnFileChange(parsedFile);
        });
    }

    private handleEmittersOnFileChange(uploadedFile: ISpreadSheet): void {
        // emitting false incase of file equals null which means deletion
        this.fileChanged.emit(uploadedFile != null);

        if (
            this.incomeStatementCompleted &&
            this.balanceCompleted &&
            this.isFormControlsComplete() &&
            uploadedFile != null
        ) {
            this.balanceFinancialStatementSourceOutput.emit(this.balanceFinancialStatementSource);
            this.incomeFinancialStatementSourceOutput.emit(this.incomeFinancialStatementSource);
            this.importFormCompleted.emit(this.isSameSheetSelectionIsComplete());
        } else {
            this.importFormCompleted.emit(false);
        }
    }

    onBalanceImportDefinitionCompleted(balanceImportDefinition: IFinancialStatementImportDefinition | boolean): void {
        this.priorYearUnmatchableAccountsDetectedBalanceFixed = false;
        this.priorYearUnmatchableAccountsDetectedBalanceFixedColNo = undefined;
        if (balanceImportDefinition !== false) {
            // clearing cell click after balance definition completed to not send it to income definition
            this.clickedCell = null;
            this.balanceCompleted = true;
            this.showIncomeImportDefinition = true;
            this.balanceFinancialStatementSource.importDefinition =
                balanceImportDefinition as IFinancialStatementImportDefinition;
            this.currentFilledBalanceFinancialStatementImportDefinition =
                cloneDeep<IFinancialStatementImportDefinition>(
                    balanceImportDefinition as IFinancialStatementImportDefinition,
                );
            this.handleSameSheetSelectionForBalanceAndIncome(false);
            this.definitionToCopy = balanceImportDefinition as IFinancialStatementImportDefinition;
            this.priorYearUnmatchableAccountsDetectedBalance = false;
            this.setAccounts(this.balanceFinancialStatementSource);

            // we add this if condition because form can't be completed before income statement is completed
            if (this.incomeStatementCompleted && this.isFormControlsComplete()) {
                this.showInterpretation = true;
                this.importFormCompleted.emit(this.isSameSheetSelectionIsComplete());
                this.balanceFinancialStatementSourceOutput.emit(this.balanceFinancialStatementSource);
                this.incomeFinancialStatementSourceOutput.emit(this.incomeFinancialStatementSource);
            }
        } else {
            this.balanceCompleted = false;
            this.importFormCompleted.emit(false);
        }
    }

    isSameSheetSelectionIsComplete() {
        if (this.sameSheetFlag) {
            if (
                this.formGroup.get('balanceAccountsStartRow').value &&
                this.formGroup.get('balanceAccountsEndRow').value &&
                this.formGroup.get('incomeAccountsStartRow').value &&
                this.formGroup.get('incomeAccountsEndRow').value
            ) {
                return true;
            } else {
                return false;
            }
        } else {
            return true;
        }
    }

    onIncomeImportDefinitionCompleted(incomeImportDefinition: IFinancialStatementImportDefinition | boolean): void {
        this.priorYearUnmatchableAccountsDetectedIncomeFixed = false;
        this.priorYearUnmatchableAccountsDetectedIncomeFixedColNo = undefined;
        if (incomeImportDefinition !== false) {
            this.incomeStatementCompleted = true;
            this.incomeFinancialStatementSource.importDefinition =
                incomeImportDefinition as IFinancialStatementImportDefinition;
            this.currentFilledIncomeFinancialStatementImportDefinition = cloneDeep<IFinancialStatementImportDefinition>(
                incomeImportDefinition as IFinancialStatementImportDefinition,
            );
            this.handleSameSheetSelectionForBalanceAndIncome(true);
            this.priorYearUnmatchableAccountsDetectedIncome = false;
            this.setAccounts(this.incomeFinancialStatementSource);
            if (this.isFormControlsComplete()) {
                this.importFormCompleted.emit(this.isSameSheetSelectionIsComplete());
                this.showInterpretation = true;
                this.incomeFinancialStatementSourceOutput.emit(this.incomeFinancialStatementSource);
                this.balanceFinancialStatementSourceOutput.emit(this.balanceFinancialStatementSource);
            }
        } else {
            this.incomeStatementCompleted = false;
            this.importFormCompleted.emit(false);
        }
    }

    onTemplateSpecialFlisFormCompleted(isCompleted: boolean): void {
        this.specialFlisFormCompleted = isCompleted;
        if (!isCompleted) {
            this.importFormCompleted.emit(false);
        } else {
            this.checkFormCompletion();
        }
    }

    onSpecialFlisSelectionChange(specialFlis: SpecialFLIs): void {
        if (this.canUseEquity || this.canUseUncovershorfall) {
            this.importedTaxonomy.rawBalanceTemplate.equityGroupName = specialFlis.equityGroupNameId;
        }

        if (this.canUseUncovershorfall) {
            this.importedTaxonomy.rawBalanceTemplate.uncoveredShortfallAssetsFli = specialFlis.uncoverShortFallAssetsId;
            this.importedTaxonomy.rawBalanceTemplate.uncoveredShortfallLiabilitiesFli =
                specialFlis.uncoverShortFallLiabilitiesId;
        }

        this.importedTaxonomy.rawBalanceTemplate.profitOfTheYear = specialFlis.profitOfTheYearId;

        this.importedTaxonomy.rawBalanceTemplate.annualSurplusFli = specialFlis.annualSurplusId;

        this.importedTaxonomyConstructed.emit(this.importedTaxonomy);
    }

    onSpreadSheetClick(clickedCell: ISpreadSheetClick): void {
        this.clickedCell = clickedCell;
        debug(this.clickedCell);
    }

    setInterpretationFormValue(interpretationData: IFinancialStatementInterpretation): void {
        this.balanceFinancialStatementSource.interpretation = interpretationData;
        this.incomeFinancialStatementSource.interpretation = interpretationData;
        this.balanceFinancialStatementSourceOutput.emit(this.balanceFinancialStatementSource);
        this.incomeFinancialStatementSourceOutput.emit(this.incomeFinancialStatementSource);
    }

    setAccountFilterFormValue(accountFilterData: IAccountFilter): void {
        this.balanceFinancialStatementSource.accountsFilter = accountFilterData;
        this.incomeFinancialStatementSource.accountsFilter = accountFilterData;
        this.priorYearUnmatchableAccountsDetectedBalance = false;
        this.priorYearUnmatchableAccountsDetectedIncome = false;
        this.setAccounts(this.balanceFinancialStatementSource);
        this.setAccounts(this.incomeFinancialStatementSource);
        this.balanceFinancialStatementSourceOutput.emit(this.balanceFinancialStatementSource);
        this.incomeFinancialStatementSourceOutput.emit(this.incomeFinancialStatementSource);
    }

    onAccountFilterFormStatusChange(isFormValid: boolean): void {
        this.isAccountFilterFormValid = isFormValid;
        this.checkFormCompletion();
    }

    private updateDisplayAccounts(): void {
        const allAccounts = [
            ...this.unmatchableBalanceAccounts,
            ...this.unmatchableIncomeAccounts,
            ...this.newShareholderBalanceAccounts,
            ...this.newShareholderIncomeAccounts,
            ...this.accountsWhichExistInPreviousYearMoreThanThisYearBalance,
            ...this.accountsWhichExistInPreviousYearMoreThanThisYearIncome,
        ];
        debug('All Accounts before filtering duplicates: ', allAccounts);
        const uniqueAccounts = Array.from(
            allAccounts
                .reduce((map, account) => {
                    let key = `${account.accountNumber}|${account.name}|${account.closingBalance}`;
                    if (account.shareholder) {
                        key += `|${account.shareholder}`;
                    }
                    if (!map.has(key)) {
                        map.set(key, account);
                    }
                    return map;
                }, new Map<string, IAccount>())
                .values(),
        );

        this.displayAccounts = uniqueAccounts.sort((a, b) =>
            (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }),
        );
    }

    private setAccounts(financialStatementSource: IFinancialStatementSource): void {
        let balanceSpreadSheet: ISpreadSheet;
        let incomeSpreadSheet: ISpreadSheet;
        const filter = financialStatementSource.accountsFilter;

        if (this.importBalanceAndIncomeSeparately) {
            balanceSpreadSheet = this.balanceSpreadSheetData;
            incomeSpreadSheet = this.incomeSpreadSheetData;
        } else {
            balanceSpreadSheet = this.spreadSheetData;
            incomeSpreadSheet = this.spreadSheetData;
        }

        // check if there is already something saved. If yes, then we need to get the already defined summarized accounts, so that we can merge them 
        const currentSummarizedAccountsSet = new Set<string>();
        if (this.activeAnnualReport != null && this.activeAnnualReport.finStatements != null && this.activeAnnualReport.finStatements.length > 0) {
            const currentAccounts = this.activeAnnualReport.finStatements[0].templateAndAccountsMapping?.accounts;

            for (const account of currentAccounts) {
                if (account.isSummarized) {
                    currentSummarizedAccountsSet.add(account.accountNumber);
                }
            }
        }

        if (
            financialStatementSource.type === FinancialStatementTypes.BALANCE &&
            balanceSpreadSheet != null &&
            balanceSpreadSheet.sheets.length !== 0 &&
            financialStatementSource.importDefinition != null
        ) {
            this.balanceAccounts = FinancialStatementImportFormHelper.getAccountsFromSpreadsheet(
                financialStatementSource,
                this.noPreviousYearData,
                this.isPreviousYearDataTakenFromPreviousBalanceAnnualReport,
                this.previousBalanceAccounts,
                this.previousBalanceSwitchAccounts,
                balanceSpreadSheet,
                AccountType.BALANCE,
                this.formGroup.get('balanceAccountsStartRow').value,
                this.formGroup.get('balanceAccountsEndRow').value,
                false,
                currentSummarizedAccountsSet
            );

            // Check if this logic is correct for both balance and income statements
            /* - Display an warning message in case there are two accounts with the same account number **AND**
                - the account is not a shareholder account
                - the account is not a previous year balance switch account
            - Provide the user the possibility to select a previous year column
            */
            this.unmatchableBalanceAccounts = [];
            this.newShareholderBalanceAccounts = [];

            if (this.isPreviousYearDataTakenFromPreviousBalanceAnnualReport) {
                // Check balance
                let { unmatchableAccounts, newShareholderAccounts } = FinancialStatementImportFormHelper.getAccountUnmatchableDuplicateAccounts(
                    this.balanceAccounts,
                    this.previousBalanceAccounts,
                    this.previousBalanceSwitchAccounts,
                    financialStatementSource
                );

                if (financialStatementSource.importDefinition.previousYearColNo != null && (unmatchableAccounts.length > 0 || newShareholderAccounts.length > 0)) {
                    this.balanceAccounts = FinancialStatementImportFormHelper.getAccountsFromSpreadsheet(
                        financialStatementSource,
                        this.noPreviousYearData,
                        this.isPreviousYearDataTakenFromPreviousBalanceAnnualReport,
                        this.previousBalanceAccounts,
                        this.previousBalanceSwitchAccounts,
                        balanceSpreadSheet,
                        AccountType.BALANCE,
                        this.formGroup.get('balanceAccountsStartRow').value,
                        this.formGroup.get('balanceAccountsEndRow').value,
                        true,
                        currentSummarizedAccountsSet
                    );

                    // Re-check unmatchable accounts after setting previous year column
                    const rechecked = FinancialStatementImportFormHelper.getAccountUnmatchableDuplicateAccounts(
                        this.balanceAccounts,
                        this.previousBalanceAccounts,
                        this.previousBalanceSwitchAccounts,
                        financialStatementSource,
                        true
                    );
                    unmatchableAccounts = rechecked.unmatchableAccounts;
                    newShareholderAccounts = rechecked.newShareholderAccounts;

                    this.priorYearUnmatchableAccountsDetectedBalanceFixed = true;
                    this.priorYearUnmatchableAccountsDetectedBalanceFixedColNo = financialStatementSource.importDefinition.previousYearColNo.name;
                }

                // APPLY FILTER to unmatchable and shareholder accounts for UI consistency
                this.unmatchableBalanceAccounts = unmatchableAccounts.filter(acc =>
                    FinancialStatementImportFormHelper.isMatchesFilter(filter, acc)
                );
                this.newShareholderBalanceAccounts = newShareholderAccounts.filter(acc =>
                    FinancialStatementImportFormHelper.isMatchesFilter(filter, acc)
                );
                this.priorYearUnmatchableAccountsDetectedBalance = this.unmatchableBalanceAccounts.length > 0;
            }

            this.updateDisplayAccounts();

        } else if (
            financialStatementSource.type === FinancialStatementTypes.INCOME &&
            incomeSpreadSheet != null &&
            incomeSpreadSheet.sheets.length !== 0 &&
            financialStatementSource.importDefinition != null
        ) {

            let isPreviousDataForIncomeStatementTakenFromPreviousAnnualReport = false;
            // For older annual reports that have isPreviousYearDataTakenFromPreviousIncomeAnnualReport undefined, we rely on isPreviousYearDataTakenFromPreviousBalanceAnnualReport
            if ((this.isPreviousYearDataTakenFromPreviousIncomeAnnualReport == null && this.isPreviousYearDataTakenFromPreviousBalanceAnnualReport) || this.isPreviousYearDataTakenFromPreviousIncomeAnnualReport) {
                isPreviousDataForIncomeStatementTakenFromPreviousAnnualReport = true;
            }

            this.incomeAccounts = FinancialStatementImportFormHelper.getAccountsFromSpreadsheet(
                financialStatementSource,
                this.noPreviousYearData,
                isPreviousDataForIncomeStatementTakenFromPreviousAnnualReport,
                this.previousIncomeAccounts,
                this.previousBalanceSwitchAccounts,
                incomeSpreadSheet,
                AccountType.INCOME,
                this.formGroup.get('incomeAccountsStartRow').value,
                this.formGroup.get('incomeAccountsEndRow').value,
                false,
                currentSummarizedAccountsSet
            );

            // Check if this logic is correct for both balance and income statements
            /* - Display an warning message in case there are two accounts with the same account number **AND**
                - the account is not a shareholder account
                - the account is not a previous year balance switch account
            - Provide the user the possibility to select a previous year column
            */
            this.unmatchableIncomeAccounts = [];
            this.newShareholderIncomeAccounts = [];


            if (isPreviousDataForIncomeStatementTakenFromPreviousAnnualReport) {
                // Check income
                let { unmatchableAccounts, newShareholderAccounts } = FinancialStatementImportFormHelper.getAccountUnmatchableDuplicateAccounts(
                    this.incomeAccounts,
                    this.previousIncomeAccounts,
                    [],
                    financialStatementSource
                );

                if (financialStatementSource.importDefinition.previousYearColNo != null && (unmatchableAccounts.length > 0 || newShareholderAccounts.length > 0)) {
                    this.incomeAccounts = FinancialStatementImportFormHelper.getAccountsFromSpreadsheet(
                        financialStatementSource,
                        this.noPreviousYearData,
                        isPreviousDataForIncomeStatementTakenFromPreviousAnnualReport,
                        this.previousIncomeAccounts,
                        this.previousBalanceSwitchAccounts,
                        incomeSpreadSheet,
                        AccountType.INCOME,
                        this.formGroup.get('incomeAccountsStartRow').value,
                        this.formGroup.get('incomeAccountsEndRow').value,
                        true,
                        currentSummarizedAccountsSet
                    );

                    // Re-check unmatchable accounts after setting previous year column

                    const rechecked = FinancialStatementImportFormHelper.getAccountUnmatchableDuplicateAccounts(
                        this.incomeAccounts,
                        this.previousIncomeAccounts,
                        [],
                        financialStatementSource,
                        true
                    );
                    unmatchableAccounts = rechecked.unmatchableAccounts;
                    newShareholderAccounts = rechecked.newShareholderAccounts;

                    this.priorYearUnmatchableAccountsDetectedIncomeFixed = true;

                    this.priorYearUnmatchableAccountsDetectedIncomeFixedColNo = financialStatementSource.importDefinition.previousYearColNo.name;
                }

                // APPLY FILTER to unmatchable and shareholder accounts for UI consistency
                this.unmatchableIncomeAccounts = unmatchableAccounts.filter(acc =>
                    FinancialStatementImportFormHelper.isMatchesFilter(filter, acc)
                );
                this.newShareholderIncomeAccounts = newShareholderAccounts.filter(acc =>
                    FinancialStatementImportFormHelper.isMatchesFilter(filter, acc)
                );
                this.priorYearUnmatchableAccountsDetectedIncome = this.unmatchableIncomeAccounts.length > 0;
            }

            this.accountsWhichExistInPreviousYearMoreThanThisYearBalance = FinancialStatementImportFormHelper.getAccountWhichExistsInPreviousYearMoreThanThisYear(
                this.balanceAccounts,
                this.previousBalanceAccounts,
            );
            this.accountsWhichExistInPreviousYearMoreThanThisYearIncome = FinancialStatementImportFormHelper.getAccountWhichExistsInPreviousYearMoreThanThisYear(
                this.incomeAccounts,
                this.previousIncomeAccounts,
            );

            this.updateDisplayAccounts();
        }
        this.allAccounts = this.balanceAccounts.concat(this.incomeAccounts);
        debug(this.allAccounts, 'All Accounts after setting from spreadsheet');
        let accountsToEmit: IAccount[] = [];
        if (
            !FinancialStatementImportFormHelper.isEmptyObject(financialStatementSource.accountsFilter) &&
            !FinancialStatementImportFormHelper.isEmptyObject(financialStatementSource.importDefinition)
        ) {
            this.filteredAccounts = this.getFilteredAccounts(filter);
            accountsToEmit = cloneDeep<IAccount[]>(this.filteredAccounts);
            this.setHighlightedRows(financialStatementSource);
        } else {
            accountsToEmit = cloneDeep<IAccount[]>(this.allAccounts);
        }
        this.accountsToMap.emit(accountsToEmit);
    }

    private getFilteredAccounts(filter: IAccountFilter): IAccount[] {
        const filteredAccounts: IAccount[] = [];
        if (filter) {
            this.allAccounts.forEach((account: IAccount) => {
                if (FinancialStatementImportFormHelper.isMatchesFilter(filter, account)) {
                    filteredAccounts.push(account);
                }
            });
        }
        return filteredAccounts;
    }

    handleSameSheetSelectionForBalanceAndIncome(isIncome: boolean = false): void {
        if (
            this.balanceFinancialStatementSource.importDefinition &&
            this.incomeFinancialStatementSource.importDefinition
        ) {
            if (
                !this.importBalanceAndIncomeSeparately &&
                this.balanceFinancialStatementSource.importDefinition.sheetName.name ===
                this.incomeFinancialStatementSource.importDefinition.sheetName.name
            ) {
                this.sameSheetFlag = true;

                this.formGroup.setValue({
                    incomeAccountsStartRow: this.incomeFinancialStatementSource.singleSheetAccountsRanges ? this.incomeFinancialStatementSource.singleSheetAccountsRanges.start : null,
                    incomeAccountsEndRow: this.incomeFinancialStatementSource.singleSheetAccountsRanges ? this.incomeFinancialStatementSource.singleSheetAccountsRanges.end : null,
                    balanceAccountsStartRow: this.balanceFinancialStatementSource.singleSheetAccountsRanges ? this.balanceFinancialStatementSource.singleSheetAccountsRanges.start : null,
                    balanceAccountsEndRow: this.balanceFinancialStatementSource.singleSheetAccountsRanges ? this.balanceFinancialStatementSource.singleSheetAccountsRanges.end : null,
                });
            } else {
                this.sameSheetFlag = false;
                // resetting single sheet form controls incase user uploaded first a single sheet and these controls had values
                this.formGroup.setValue({
                    balanceAccountsStartRow: null,
                    balanceAccountsEndRow: null,
                    incomeAccountsStartRow: null,
                    incomeAccountsEndRow: null,
                });
            }
        }
    }

    setHighlightedRows(financialStatementSource: IFinancialStatementSource): void {
        const rowsToHighlight: IHighlightedRows = {
            sheetName: financialStatementSource.importDefinition.sheetName.name,
            rows: [],
            unmatchableAccountRows: [],
        };
        // get the difference between all accounts(of the same type) and filtered accounts
        // the difference will be the excluded accounts (highlighted)
        const excludedAccounts = this.allAccounts.filter(
            (account) =>
                !this.filteredAccounts.includes(account) &&
                account.type ===
                FinancialStatementImportFormHelper.getAccountTypeFromFinancialStatementSourceType(
                    financialStatementSource.type,
                ),
        );

        excludedAccounts.forEach((account) => {
            rowsToHighlight.rows.push(account.rowNo);
        });
        if (financialStatementSource.type === FinancialStatementTypes.BALANCE) {
            this.balanceHighlightedRows = rowsToHighlight;
            rowsToHighlight.unmatchableAccountRows = this.unmatchableBalanceAccounts.map((account) => account.rowNo);
        } else {
            this.incomeHighlightedRows = rowsToHighlight;
            rowsToHighlight.unmatchableAccountRows = this.unmatchableIncomeAccounts.map((account) => account.rowNo);
        }
    }

    private monitorFormGroupChanges(): void {
        merge(this.formGroup.valueChanges, this.formGroup.statusChanges)
            .pipe(
                debounceTime(USER_INPUT_DEBOUNCE_TIME),
                distinctUntilChanged((previousChanges, currentChanges) =>
                    fastDeepEqual(previousChanges, currentChanges),
                ),
                untilDestroyed(this),
            )
            .subscribe(() => this.checkFormCompletion());
    }

    checkFormCompletion() {
        if (
            this.isFormControlsComplete() &&
            this.incomeStatementCompleted &&
            this.balanceCompleted &&
            this.specialFlisFormCompleted &&
            this.isAccountFilterFormValid
        ) {
            this.showInterpretation = true;
            // Saving account ranges in the state

            this.balanceFinancialStatementSource.singleSheetAccountsRanges = {
                start: this.formGroup.get('balanceAccountsStartRow').value,
                end: this.formGroup.get('balanceAccountsEndRow').value,
            };

            this.incomeFinancialStatementSource.singleSheetAccountsRanges = {
                start: this.formGroup.get('incomeAccountsStartRow').value,
                end: this.formGroup.get('incomeAccountsEndRow').value,
            };

            this.priorYearUnmatchableAccountsDetectedBalance = false;
            this.priorYearUnmatchableAccountsDetectedIncome = false;
            this.setAccounts(this.balanceFinancialStatementSource);
            this.setAccounts(this.incomeFinancialStatementSource);

            this.importFormCompleted.emit(this.isSameSheetSelectionIsComplete());

            this.balanceFinancialStatementSourceOutput.emit(this.balanceFinancialStatementSource);
            this.incomeFinancialStatementSourceOutput.emit(this.incomeFinancialStatementSource);
        } else {
            this.importFormCompleted.emit(false);
        }
    }

    /**
     * Flushes the latest same-sheet row input values into the emitted financial statement sources
     * by forcing a synchronous completion check before the parent save flow reads them.
     */
    flushPendingFormState(): void {
        this.checkFormCompletion();
    }

    getFormValidationState(): FormValidationState {
        return {
            formControlsComplete: this.isFormControlsComplete(),
            incomeStatementCompleted: this.incomeStatementCompleted,
            balanceCompleted: this.balanceCompleted,
            specialFlisFormCompleted: this.specialFlisFormCompleted,
            accountFilterFormValid: this.isAccountFilterFormValid,
            sameSheetSelectionComplete: this.isSameSheetSelectionIsComplete(),
        };
    }

    isFormControlsComplete(): boolean {
        const errors = FinancialStatementImportFormHelper.rangeCheckValidator(this.formGroup);
        if (this.sameSheetFlag === true) {
            return Object.keys(errors).length === 0;
        }
        return true;
    }

    stopPropagation(event: MouseEvent): void {
        event.stopPropagation();
    }

    triggerLoseFocus(): void {
        this.provider.dispatchFinancialStatmentImportLoseFocus();
    }

    checkStateValues(): void {
        if (this.balanceFinancialStatementSource == null) {
            this.provider
                .getBalanceFinancialStatementSource$()
                .pipe(
                    distinctUntilChanged((previousFormChanges, currentFormChanges) =>
                        fastDeepEqual(previousFormChanges, currentFormChanges),
                    ),
                    untilDestroyed(this),
                )
                .subscribe((data: IFinancialStatementSource) => {
                    this.balanceFinancialStatementSource = data;
                });
        }
        if (this.incomeFinancialStatementSource == null) {
            this.provider
                .getIncomeFinancialStatementSource$()
                .pipe(
                    distinctUntilChanged((previousFormChanges, currentFormChanges) =>
                        fastDeepEqual(previousFormChanges, currentFormChanges),
                    ),
                    untilDestroyed(this),
                )
                .subscribe((data: IFinancialStatementSource) => {
                    this.incomeFinancialStatementSource = data;
                });
        }
    }

    handleImportOptions(importOptions: IFinancialStatementImportOptions): void {
        if (importOptions != null) {
            this.importBalanceAndIncomeSeparately = importOptions.importBalanceAndIncomeSeparately;
        }
    }

    handleSelectedAnnualReportIdToAutofillFrom(
        annualReportIdWithAutofillOptions: Record<AutofillOptions, string>,
    ): void {
        this.selectedAnnualReportWithAutofillOptions.emit(annualReportIdWithAutofillOptions);
    }
}